import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import Properties from "@/pages/Properties";
import Deals from "@/pages/Deals";
import DealDetail from "@/pages/DealDetail";
import Vendors from "@/pages/Vendors";
import UsersPage from "@/pages/Users";
import UserDetailPage from "@/pages/UserDetail";
import Maintenance from "@/pages/Maintenance";
import MaintenanceReport from "@/pages/MaintenanceReport";
import Invoices from "@/pages/Invoices";
import Payables from "@/pages/Payables";
// `Materials` page is now embedded as a tab inside `ProductCatalog`; the
// standalone /materials route redirects to /catalog?tab=by-vendor. Do not
// import here — the import lives inside ProductCatalog.jsx.
import Library from "@/pages/Library";
import BooksCOA from "@/pages/BooksCOA";
import CoiReminders from "@/pages/CoiReminders";
import PublicGallery from "@/pages/PublicGallery";
import ProposalSign from "@/pages/ProposalSign";
import WorkOrderSign from "@/pages/WorkOrderSign";
import MagicLinkConsume from "@/pages/MagicLinkConsume";
import LeadIntake from "@/pages/LeadIntake";
import TimeClock from "@/pages/TimeClock";
import SubClock from "@/pages/SubClock";
import SubAttendance from "@/pages/SubAttendance";
import TimeAdmin from "@/pages/TimeAdmin";
import JobCosts from "@/pages/JobCosts";
import WinLoss from "@/pages/WinLoss";
import Warranties from "@/pages/Warranties";
import ARAging from "@/pages/ARAging";
import CrewSchedule from "@/pages/CrewSchedule";
import SinglePly from "@/pages/SinglePly";
import CustomEstimates from "@/pages/CustomEstimates";
import CustomEstimateEditor from "@/pages/CustomEstimateEditor";
import EstimateSign from "@/pages/EstimateSign";
import ServiceTickets from "@/pages/ServiceTickets";
import ServiceTemplates from "@/pages/ServiceTemplates";
import TrashPage from "@/pages/Trash";
import Profile from "@/pages/Profile";
import Assessments from "@/pages/Assessments";
import ProductCatalog from "@/pages/ProductCatalog";
import Calculator from "@/pages/Calculator";
import AssessmentEditor from "@/pages/AssessmentEditor";
import Calendar from "@/pages/Calendar";
import Tasks from "@/pages/Tasks";
import Integrations from "@/pages/Integrations";
import WebsiteIntegration from "@/pages/WebsiteIntegration";
import EquipmentRates from "@/pages/EquipmentRates";
import CompanyInfo from "@/pages/CompanyInfo";
import MyCommissions from "@/pages/MyCommissions";
import Commissions from "@/pages/Commissions";
import CommissionSign from "@/pages/CommissionSign";
import Guide from "@/pages/Guide";
import TrainingGuides from "@/pages/TrainingGuides";
import ScopeBullets from "@/pages/ScopeBullets";
import SystemMap from "@/pages/SystemMap";
import Employees from "@/pages/Employees";
import Schedule from "@/pages/Schedule";
import FieldCapture from "@/pages/FieldCapture";
import SetPassword from "@/pages/SetPassword";
import Scopes from "@/pages/Scopes";
import PWAControls from "@/components/PWAControls";
import "@/App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Field-sub role gets ZERO CRM access — every protected route redirects
  // them to `/field` (which is a public route above and has its own auth
  // check inside FieldCapture).
  if (user.role === "field_sub") return <Navigate to="/field" replace />;
  return children;
};

/**
 * On phone-sized viewports OR mobile user-agents, every protected CRM route
 * auto-redirects to /field — the stripped-down project-list + camera UI. The
 * full CRM is desktop/tablet only. Adding `?desktop=1` to any URL on a phone
 * forces the full CRM through (preference is remembered for the tab session).
 *
 * Feb 2026 — Simplified to STRICT field-only mode: phones only see the
 * photo/video capture UI + the (soon-to-arrive) leave-a-note input. Everything
 * else (Dashboard, Deals, Wrap-Up, etc.) requires the `?desktop=1` override.
 */
const MobileGate = ({ children }) => {
  if (typeof window === "undefined") return children;
  // Honour explicit user override first.
  const urlForce = new URLSearchParams(window.location.search).get("desktop") === "1";
  if (urlForce) sessionStorage.setItem("force_desktop_crm", "1");
  if (sessionStorage.getItem("force_desktop_crm") === "1") return children;
  // Detect phone: small viewport OR a mobile UA string (catches phones that
  // render the desktop layout via a zoomed-out viewport).
  const ua = navigator.userAgent || "";
  const uaIsPhone = /iPhone|Android.+Mobile|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isPhone = uaIsPhone || window.innerWidth < 768;
  if (!isPhone) return children;
  return <Navigate to="/field" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PWAControls />
          <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            {/* Public photo-share gallery — NO auth required */}
            <Route path="/share/photos/:token" element={<PublicGallery />} />
            <Route path="/sign/:token" element={<ProposalSign />} />
            <Route path="/estimate-sign/:token" element={<EstimateSign />} />
            <Route path="/work-order/sign/:token" element={<WorkOrderSign />} />
            <Route path="/commissions/sign/:token" element={<CommissionSign />} />
            <Route path="/m/:token" element={<MagicLinkConsume />} />
            <Route path="/intake" element={<LeadIntake />} />
            <Route path="/timeclock" element={<TimeClock />} />
            <Route path="/subclock" element={<SubClock />} />
            {/* Public password-set page (subcontractor invite flow) */}
            <Route path="/set-password" element={<SetPassword />} />
            {/* Standalone full-screen mobile camera capture (no sidebar Layout) */}
            <Route path="/field" element={<FieldCapture />} />
            <Route
              element={
                <ProtectedRoute>
                  <MobileGate>
                    <Layout />
                  </MobileGate>
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/properties" element={<Properties />} />
              <Route path="/projects" element={<Deals />} />
              <Route path="/projects/:id" element={<DealDetail />} />
              <Route path="/deals" element={<Deals />} />
              <Route path="/deals/:id" element={<DealDetail />} />
              <Route path="/vendors" element={<Vendors kind="Vendor" />} />
              <Route path="/subcontractors" element={<Vendors kind="Subcontractor" />} />
              <Route path="/coi-reminders" element={<CoiReminders />} />
              <Route path="/trash" element={<TrashPage />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/service" element={<ServiceTickets />} />
              <Route path="/maintenance/report/:dealId/:visitId" element={<MaintenanceReport />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/payables" element={<Payables />} />
              <Route path="/materials" element={<Navigate to="/catalog?tab=by-vendor" replace />} />
              <Route path="/library" element={<Library />} />
              <Route path="/assessments" element={<Assessments />} />
              <Route path="/scopes" element={<Scopes />} />
              <Route path="/catalog" element={<ProductCatalog />} />
              <Route path="/calculator" element={<Calculator />} />
              <Route path="/assessments/new" element={<AssessmentEditor />} />
              <Route path="/assessments/:id" element={<AssessmentEditor />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/settings/integrations" element={<Integrations />} />
              <Route path="/settings/website" element={<WebsiteIntegration />} />
              <Route path="/time" element={<TimeAdmin />} />
              <Route path="/reports/job-costs" element={<JobCosts />} />
              <Route path="/reports/win-loss" element={<WinLoss />} />
              <Route path="/reports/warranties" element={<Warranties />} />
              <Route path="/reports/ar-aging" element={<ARAging />} />
              <Route path="/crew-schedule" element={<CrewSchedule />} />
              <Route path="/single-ply" element={<SinglePly />} />
              <Route path="/custom-estimates" element={<CustomEstimates />} />
              <Route path="/custom-estimates/:id" element={<CustomEstimateEditor />} />
              <Route path="/settings/equipment-rates" element={<EquipmentRates />} />
              <Route path="/settings/company-info" element={<CompanyInfo />} />
              <Route path="/commissions/mine" element={<MyCommissions />} />
              <Route path="/commissions" element={<Commissions />} />
              <Route path="/guide" element={<Guide />} />
              <Route path="/training" element={<TrainingGuides />} />
              <Route path="/sub-attendance" element={<SubAttendance />} />
              <Route path="/settings/scope-bullets" element={<ScopeBullets />} />
              <Route path="/settings/service-templates" element={<ServiceTemplates />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/admin/system-map" element={<SystemMap />} />
              <Route path="/settings/schedule" element={<Schedule />} />
              <Route path="/books" element={<BooksCOA />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/:id" element={<UserDetailPage />} />
              <Route path="/profile" element={<Profile />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;

